import {BaseModel} from '../base/BaseModel';

/**
 * @class AggregatedUserNotificationModel
 * @extends BaseModel
 *
 */
export class AggregatedUserNotificationModel extends BaseModel {

    get defaults () {
        return {
            title: undefined,
            description: undefined,
            payload: undefined,
            aggregationFromDate: undefined,
            aggregationTillDate: undefined,
        };
    }

    initialize () {
        this.microservice = 'notifications';
        this.resource = 'aggregated-user-notifications';
    }

    toViewJSON () {
        return _.extend(this.getConfig(),{
            type: this.getType(),
            farmUuid: this.getFarmUuid(),
            title: this.getTitle(),
            description: this.getDescription(),
            badgeCount: this.getBadgeCount(),
            legacyFarmName: this.get('title'),
        });
    }

    /**
     * @returns {Moment}
     */
    getDate () {
        return BaseModel.parseDate(this.get('aggregationFromDate'));
    }

    /**
     * @returns {string}
     */
    getDateString () {
        return this.getDate().startOf('day').format(__('_DATE_FORMAT_LONG_MONTH'));
    }

    /**
     * @returns {string}
     */
    getType () {
        return this.get('payload').type;
    }

    /**
     * @returns {string}
     */
    getFarmUuid () {
        return this.get('payload').farmUuid;
    }

    /**
     * @returns {string}
     */
    getTitle () {
        let titleParts = this.get('description').split('\n');

        return titleParts[0];
    }

    /**
     * @returns {string}
     */
    getDescription () {
        let titleParts = this.get('description').split('\n');

        return _.tail(titleParts).join(' ');
    }

    /**
     * @returns {number}
     */
    getBadgeCount () {
        let payload = this.get('payload');
        let ids = payload.sprayingUuids ||
                payload.cropSeasonUuids ||
                payload.flightTaskUuids ||
                payload.fieldDataLayerUuids ||
                payload.farmsUuids ||
                payload.usersUuids;

        return ids ? ids.length : 0;
    }

    /**
     * @returns {{icon: {string}}}
     */
    getConfig () {
        return _.clone(AggregatedUserNotificationModel.CONFIG[this.getType()]);
    }

    static get TYPES () {
        return {
            TASK_CREATED: 'TASK_CREATED',
            TASK_OVERDUE: 'TASK_OVERDUE',
            TASK_ASSIGNED: 'TASK_ASSIGNED',
            TASK_SCHEDULED_TODAY: 'TASK_SCHEDULED_TODAY',
            GROWTH_STAGE_REACHED: 'GROWTH_STAGE_REACHED',
            GROWTH_STAGE_UPCOMING: 'GROWTH_STAGE_UPCOMING',
            NEW_BIOMASS_MAP_AVAILABLE: 'NEW_BIOMASS_MAP_AVAILABLE',
            NEW_POWER_ZONE_MAP_AVAILABLE: 'NEW_POWER_ZONE_MAP_AVAILABLE',
            NEW_WEED_MAP_AVAILABLE: 'NEW_WEED_MAP_AVAILABLE',
            HIGH_DISEASE_RISK_REACHED: 'HIGH_DISEASE_RISK_REACHED',
            MEDIUM_DISEASE_RISK_REACHED: 'MEDIUM_DISEASE_RISK_REACHED',
            START_MIGRATION: 'START_MIGRATION',
            NEW_FARM_SHARED: 'NEW_FARM_SHARED',
            DRONE_FLIGHT_CREATED: 'DRONE_FLIGHT_CREATED',
            FARM_ACCESS_REQUEST: 'FARM_ACCESS_REQUEST',
            INVITED_USER_SIGNSUP: 'INVITED_USER_SIGNSUP',
            PROCESSING_FAILED: 'PROCESSING_FAILED',
            SPRAY_RECOMMENDATION: 'SPRAY_RECOMMENDATION',
            WATER_RECOMMENDATION: 'WATER_RECOMMENDATION',
            FERTILIZER_RECOMMENDATION: 'FERTILIZER_RECOMMENDATION',
            INSECTICIDE_RECOMMENDATION: 'INSECTICIDE_RECOMMENDATION',
            FUNGICIDE_RECOMMENDATION: 'FUNGICIDE_RECOMMENDATION',
            PLANT_GROWTH_REGULATOR_RECOMMENDATION: 'PLANT_GROWTH_REGULATOR_RECOMMENDATION',
            CROP_SEASON_AUTO_CLOSURE: 'CROP_SEASON_AUTO_CLOSURE',
            PROCESSING_DENIED_QUOTA_EXHAUSTED: 'PROCESSING_DENIED_QUOTA_EXHAUSTED',
            SEEDING_TASK_CREATED: 'SEEDING_TASK_CREATED',
            SEEDING_TASK_ASSIGNED: 'SEEDING_TASK_ASSIGNED',
            SCOUTING_TASK_ASSIGNED: 'SCOUTING_TASK_ASSIGNED'
        };
    }

    static get CONFIG () {
        return {
            TASK_CREATED: {
                icon: 'tasks-filled',
                urlKey: 'TASKS',
            },
            TASK_OVERDUE: {
                icon: 'tasks-filled',
                urlKey: 'TASKS',
            },
            TASK_PLANNED_DATE_UPDATED: {
                icon: 'tasks-filled',
                urlKey: 'TASKS',
            },
            TASK_SCHEDULED_TODAY: {
                icon: 'tasks-filled',
                urlKey: 'TASKS',
            },
            TASK_ASSIGNED: {
                icon: 'tasks-filled',
                urlKey: 'TASKS',
            },
            GROWTH_STAGE_REACHED: {
                icon: 'crop',
                urlKey: 'STATUS',
            },
            GROWTH_STAGE_UPCOMING: {
                icon: 'crop',
                urlKey: 'STATUS',
            },
            NEW_BIOMASS_MAP_AVAILABLE: {
                icon: 'biomassmap',
                urlKey: 'STATUS',
            },
            NEW_POWER_ZONE_MAP_AVAILABLE: {
                icon: 'powerzonemap',
                urlKey: 'STATUS',
            },
            SPRAY_RECOMMENDATION: {
                icon: 'spray-circle',
                color: 'xarvio',
                urlKey: 'STATUS',
            },
            NEW_WEED_MAP_AVAILABLE: {
                icon: 'weed-map',
                urlKey: 'STATUS',
            },
            HIGH_DISEASE_RISK_REACHED: {
                icon: 'disease-spread-circle',
                iconColor: 'red',
                urlKey: 'STATUS',
            },
            MEDIUM_DISEASE_RISK_REACHED: {
                icon: 'disease-spread-circle',
                iconColor: 'yellow',
                urlKey: 'STATUS',
            },
            START_MIGRATION: {
                icon: 'pest',
                urlKey: 'STATUS',
            },
            NEW_FARM_SHARED: {
                icon: 'farm',
                urlKey: 'FARM_SHARING',
            },
            FARM_SHARED: {
                icon: 'farm',
                urlKey: 'FARM_SHARING',
            },
            FARM_ACCESS_REQUEST: {
                icon: 'farm',
                urlKey: 'FARM_SHARING',
            },
            INVITED_USER_SIGNSUP: {
                icon: 'farm',
                urlKey: 'FARM_SHARING',
            },
            DRONE_FLIGHT_EXECUTED: {
                icon: 'drone',
                urlKey: 'TASKS',
            },
            DRONE_FLIGHT_CREATED: {
                icon: 'drone',
                urlKey: 'TASKS',
            },
            DRONE_FLIGHT_SCHEDULE_DATE_UPDATED: {
                icon: 'drone',
                urlKey: 'TASKS',
            },
            FLIGHT_ORDER_CREATED: {
                icon: 'drone',
                urlKey: 'TASKS',
            },
            DRONE_FLIGHT_DATE_ADDED: {
                icon: 'drone',
                urlKey: 'TASKS',
            },
            DRONE_FLIGHT_DATE_UPDATED: {
                icon: 'drone',
                urlKey: 'TASKS',
            },
            PROCESSING_DENIED_QUOTA_EXHAUSTED: {
                icon: 'drone',
                urlKey: 'TASKS',
            },
            OPTIMAL_DMC_ENTERED: {
                icon: 'drymattercontent',
                urlKey: 'STATUS',
            },
            OPTIMAL_DMC_ACHIEVED: {
                icon: 'drymattercontent',
                urlKey: 'STATUS',
            },
            SUBOPTIMAL_DMC_ENTERED: {
                icon: 'drymattercontent',
                urlKey: 'STATUS',
            },
            SUBOPTIMAL_DMC_ACHIEVED: {
                icon: 'drymattercontent',
                urlKey: 'STATUS',
            },
            FERTILIZER_RECOMMENDATION: {
                icon: 'nutrition',
                urlKey: 'STATUS',
            },
            WATER_RECOMMENDATION : {
                icon: 'water-drop',
                urlKey: 'STATUS',
            },
            PLANT_GROWTH_REGULATOR_RECOMMENDATION : {
                icon: 'plant-circle-arrow-down',
                urlKey: 'STATUS',
            },
            FUNGICIDE_RECOMMENDATION : {
                icon: 'disease-spread',
                urlKey: 'STATUS',
            },
            INSECTICIDE_RECOMMENDATION : {
                icon: 'pest',
                urlKey: 'STATUS',
            },
            CROP_SEASON_AUTO_CLOSURE: {
                icon: 'crop',
                urlKey: 'HISTORY',
            },
            SEEDING_TASK_CREATED: {
                icon: 'seeding',
                urlKey: 'TASKS',
            },
            SEEDING_TASK_ASSIGNED: {
                icon: 'seeding',
                urlKey: 'TASKS',
            } ,
            SCOUTING_TASK_ASSIGNED: {
                icon: 'camera',
                urlKey: 'TASKS',
            }
        };
    }

}


// WEBPACK FOOTER //
// ./shared/model/notifications/AggregatedUserNotificationModel.js